import React from 'react';
import { Text } from '@heartinternet/hi-ui';
import { Textarea } from '@heartinternet/hi-ui';
import { Select } from '@heartinternet/hi-ui';
import { Button } from '@heartinternet/hi-ui';
import { Notice } from '@heartinternet/hi-ui';
import { Link } from '@heartinternet/hi-ui';
import { Form } from '@heartinternet/hi-ui';
import { FormField } from '@heartinternet/hi-ui';
import './FeedbackPage.css';

export type FeedbackPageState = 'form' | 'success' | 'error' | 'no-access';

export interface FeedbackPageProps {
  /** Which render state to show. Picked server-side by the CGI and passed through the template. */
  state: FeedbackPageState;
}

const RATING_OPTIONS = [
  { value: 'very-good', label: 'Very Good' },
  { value: 'good', label: 'Good' },
  { value: 'average', label: 'Average' },
  { value: 'poor', label: 'Poor' },
  { value: 'very-poor', label: 'Very Poor' },
] as const;

const CHOICE_REASON_OPTIONS = [
  { value: 'price', label: 'Price' },
  { value: 'features', label: 'Features' },
  { value: 'price-features', label: 'Price & Features' },
  { value: 'recommendation', label: 'Recommendation' },
  { value: 'other', label: 'Other - Please State Below' },
] as const;

const WOULD_RECOMMEND_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'maybe', label: 'Maybe' },
  { value: 'no', label: 'No' },
] as const;

export const FeedbackPage: React.FC<FeedbackPageProps> = ({ state }) => {
  if (state === 'no-access') {
    return (
      <div className="hi-ui-feedback-page">
        <Text variant="heading-1">Your Feedback</Text>
        <Notice variant="info">
          <Text variant="body" inline>
            You haven&apos;t bought anything yet. Please leave feedback once you&apos;ve bought
            something, or contact our sales team.
          </Text>
        </Notice>
      </div>
    );
  }

  if (state === 'success') {
    return (
      <div className="hi-ui-feedback-page">
        <Text variant="heading-1">Thank You</Text>
        <Notice variant="success" role="alert">
          <Text variant="body" inline>
            Your feedback has been sent.
          </Text>
        </Notice>
        <Link href="/manage/">Return to the Heart Internet customer area</Link>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="hi-ui-feedback-page">
        <Text variant="heading-1">Error</Text>
        <Notice variant="error" role="alert">
          <Text variant="body">
            Please ensure your email address is valid and that all fields are filled in.
          </Text>
          <Text variant="body" inline>
            Press the back button in your browser to try again.
          </Text>
        </Notice>
      </div>
    );
  }

  return (
    <div className="hi-ui-feedback-page">
      <Text variant="heading-1">Feedback</Text>
      <Text variant="body">
        Do you have some ideas about how we can improve our features? Let the right people know by
        completing our feedback form below.
      </Text>

      <Form action="/manage/feedback.cgi" method="post">
        <div className="hi-ui-feedback-page__fields">
          <FormField label="What additional features would you like to be included in your eXtend Control Panel?">
            <Textarea name="improvehostcp" rows={5} fullWidth />
          </FormField>

          <FormField label="Are there any additional CGI/PHP scripts that you would like the eXtend control panel to be able to automatically install?">
            <Textarea name="extra-cgi" rows={5} fullWidth />
          </FormField>

          <FormField label="What additional features would you like to be included in the customer Control Panel (where you are now)?">
            <Textarea name="improvecust" rows={5} fullWidth />
          </FormField>

          <FormField label="Do you have any comments or suggestions about the features in your web hosting account?">
            <Textarea name="featuresc" rows={5} fullWidth />
          </FormField>

          <FormField label="How would you rate the technical support you have received from our technical support team?">
            <Select name="support-rate" defaultValue="never-used">
              <option value="never-used">I have never contacted support</option>
              {RATING_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </FormField>

          <FormField label="How would you rate the eXtend control panel?">
            <Select name="extend-rate" defaultValue="never-used">
              <option value="never-used">I have never used eXtend</option>
              {RATING_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </FormField>

          <FormField label="How would you rate the customer control panel (where you are now)?">
            <Select name="customer-rate" defaultValue="very-good">
              {RATING_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </FormField>

          <FormField label="What was the main factor that made you choose Heart Internet?">
            <Select name="choice-reason" defaultValue="price">
              {CHOICE_REASON_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </FormField>

          <FormField label="Would you recommend Heart Internet to your friends and colleagues?">
            <Select name="would-recommend" defaultValue="yes">
              {WOULD_RECOMMEND_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </FormField>

          <FormField label="Do you have any other comments?">
            <Textarea name="comments" rows={5} fullWidth />
          </FormField>

          <FormField
            label="Customer Testimonial"
            helperText="If you would like to be included on our testimonials page please write a few sentences here explaining your experience with Heart Internet."
          >
            <Textarea name="testimonial" rows={5} fullWidth />
          </FormField>
        </div>

        <div className="hi-ui-feedback-page__submit">
          <Button type="submit" variant="primary">
            Send Feedback Now
          </Button>
        </div>
      </Form>
    </div>
  );
};
